import type { ComponentType } from "react";
import { TrustGapPost } from "@/content/blog/ai-agent-trust-gap";
import { AiVisibilityToolsPost } from "@/content/blog/ai-visibility-tracking-tools";
import { McpServerSecurityChecklistPost } from "@/content/blog/mcp-server-security-checklist";
import { WatAiAanhaaltPost } from "@/content/blog/wat-ai-aanhaalt-nederlandse-vragen";
import { WhatAiAnswersCitePost } from "@/content/blog/what-ai-answers-cite";

/**
 * The blog is a hand-kept list rather than a CMS or an MDX pipeline.
 *
 * Posts here are written, not generated, and there are few enough of them that
 * a registry is cheaper than a build step. Adding one means writing the body
 * component and adding a line below, which keeps the slug, the metadata and the
 * component impossible to get out of sync.
 */
export type BlogPost = {
	slug: string;
	title: string;
	description: string;
	/** ISO date. Drives ordering and the article schema. */
	published: string;
	modified: string;
	readingMinutes: number;
	tags: string[];
	/**
	 * Content language, as a BCP 47 tag. The site is English, so this defaults
	 * to "en" and only a translated post sets it. It drives the `lang` attribute
	 * on the article element and `og:locale`, which is what keeps a Dutch post
	 * from being read as badly written English by search engines and screen
	 * readers alike.
	 */
	lang?: "en" | "nl";
	body: ComponentType;
};

export const BLOG_POSTS: BlogPost[] = [
	{
		slug: "mcp-server-security-checklist",
		title: "MCP Server Security Checklist for AI Agents",
		description:
			"7 checks for choosing an MCP server: verify its source, install command, tools, permissions, test path, and removal plan before it reaches live data.",
		published: "2026-08-27T00:00:00Z",
		modified: "2026-08-27T00:00:00Z",
		readingMinutes: 8,
		tags: ["mcp server security checklist", "mcp", "ai agents", "security"],
		body: McpServerSecurityChecklistPost,
	},
	{
		slug: "what-ai-answers-cite",
		title: "What AI Answers Actually Cite: 21,508 Citations Analyzed",
		description:
			"ChatGPT cites brand homepages 33.1% of the time and roundups 6.8%. Perplexity is the mirror image. Which one your buyers use decides where the budget goes.",
		published: "2026-08-16T00:00:00Z",
		modified: "2026-08-16T00:00:00Z",
		readingMinutes: 6,
		tags: ["ai visibility", "geo", "citations", "distribution"],
		body: WhatAiAnswersCitePost,
	},
	{
		slug: "wat-ai-aanhaalt-nederlandse-vragen",
		lang: "nl",
		title: "Wat AI aanhaalt bij Nederlandse vragen: 4.988 citaties",
		description:
			"74,2% van de bronnen bij Nederlandstalige AI-antwoorden komt van een .nl-website. Lijstjes leveren hier 3,7% op, je eigen pagina's 43,5%.",
		published: "2026-08-16T00:00:00Z",
		modified: "2026-08-16T00:00:00Z",
		readingMinutes: 6,
		tags: ["ai zichtbaarheid", "geo", "nederland", "citaties"],
		body: WatAiAanhaaltPost,
	},
	{
		slug: "ai-visibility-tracking-tools",
		title:
			"AI Visibility Tools Compared: Tracking Your Brand in ChatGPT and Perplexity",
		description:
			"Otterly, Peec, Profound and VerifiedDR compared on engines, frequency and cost per prompt, plus how to decide in ten minutes without buying anything.",
		published: "2026-08-11T00:00:00Z",
		modified: "2026-08-11T00:00:00Z",
		readingMinutes: 8,
		tags: ["ai visibility", "geo", "brand monitoring", "tools"],
		body: AiVisibilityToolsPost,
	},
	{
		slug: "ai-agent-trust-gap",
		title: "The Trust Gap: Why AI Agents Stall Between Demo and Revenue",
		description:
			"Agent demos impress and then fail to convert. The gap is not capability, it is who carries the risk of a wrong action. Here is what closes it.",
		published: "2026-08-11T00:00:00Z",
		modified: "2026-08-11T00:00:00Z",
		readingMinutes: 7,
		tags: ["ai agents", "trust", "agent revenue", "product"],
		body: TrustGapPost,
	},
];

export function listBlogPosts(): BlogPost[] {
	return [...BLOG_POSTS].sort(
		(a, b) => new Date(b.published).getTime() - new Date(a.published).getTime(),
	);
}

export function getBlogPost(slug: string): BlogPost | null {
	return BLOG_POSTS.find((post) => post.slug === slug) ?? null;
}

export function formatBlogDate(
	value: string,
	lang: "en" | "nl" = "en",
): string {
	return new Date(value).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}
